import { includeFields } from "../constants";
import SnackbarUtils from "../components/NotistackPlus";
import isEqual from "lodash/isEqual";

export const toArray = (value: any) => {
  return Array.isArray(value) ? value : [value];
};

export const replaceParams = (url: string, context: Record<string, any>) => {
  if (!url) return "";
  for (const key in context) {
    if (context.hasOwnProperty(key)) {
      const value = context[key];
      // String#replace stringifies null/undefined into the path (".../undefined").
      if (value === undefined || value === null) {
        continue;
      }
      const pattern = "${" + key + "}";
      url = url.replace(pattern, String(value));
    }
  }
  return url;
};

export function logoutFunction() {
  localStorage.clear();
}

export function operatorName(name: any) {
  switch (name) {
    case "contains":
      return "Contains";
    case "equals":
      return "Equals";
    case "startsWith":
      return "Starts With";
    case "endsWith":
      return "Ends With";
    case "isEmpty":
      return "is Empty";
    case "isNotEmpty":
      return "is Not Empty";
    default:
      return "";
  }
}

export const getAllIds = (tree: any, result: any) => {
  if (!Array.isArray(tree)) return [];
  for (let i = 0; i < tree.length; i++) {
    const node = tree[i];
    result.push(node.id);
    if (Array.isArray(node.children)) {
      getAllIds(node.children, result);
    }
  }
  return result;
};

export const canParseToJson = (str: any) => {
  if (typeof str == "string") {
    try {
      var obj = JSON.parse(str);
      if (typeof obj == "object" && obj) {
        return true;
      } else {
        return false;
      }
    } catch (e) {
      return false;
    }
  }
};

export function getURLDomain() {
  const domain = process.env.REACT_APP_STG_DOMAIN || "";
  const splitByDomain = window.location.hostname.split(domain);
  const splitByDot = splitByDomain[0]?.split(".");
  const domainName = splitByDot[1] ? `${splitByDot[1]}.${domain}` : `${domain}`;

  return domainName;
}

export function firstLetterCapitalize(str: string) {
  if (!str) return "";
  const modifiedString = str.replace(/\b\w/g, function (match) {
    return match.toUpperCase();
  });
  return modifiedString;
}

export const removeChildWithId = (parent: any, idToRemove: string) => {
  return {
    ...parent,
    children: parent.children.filter((child: any) => child.name !== idToRemove),
  };
};

export const actionAlwaysPinnedRight = (originArr?: string[]) => {
  if (Array.isArray(originArr)) {
    const pinnedRightLen = originArr.length;
    if (originArr[pinnedRightLen - 1] !== "action") {
      const tempPinnedRight = originArr.filter((i) => i !== "action");
      tempPinnedRight.push("action");
      return tempPinnedRight;
    }
  }
  return originArr;
};

export const handleSelectedIdentifiers = (
  filterList: any,
  identifierList = [] as any[],
  allSelected?: Record<string, boolean>,
) => {
  let afterFilterList = {} as any;
  for (let key in filterList) {
    if (!identifierList.includes(key)) {
      if (Array.isArray(filterList[key])) {
        if (filterList[key].length > 0) {
          afterFilterList[key] = allSelected?.[key] ? ["all"] : filterList[key];
        }
      } else {
        afterFilterList[key] = allSelected?.[key] ? ["all"] : [filterList[key]];
      }
    }
  }
  return afterFilterList;
};

interface singleIdentifiersMeta {
  module: string;
  section: string;
  menu: string;
}

const transElementToStr = (element: any) => Array.isArray(element) ? '' : element;

export const handleSelectedIdentifiersMeta = (
  originalMeta: Record<string, any>,
  originalIdentifierMap: Record<string, any>,
) => {
  // const pickKeys = Object.keys(originalMeta);
  let resultObj: Record<string, singleIdentifiersMeta[]> = {};
  for (const key in originalIdentifierMap) {
    if (!Object.hasOwn(originalMeta, key)) {
      resultObj[`${key}::`] = [];
      continue;
    }
    const element = originalMeta[key];
    const field = element?.field ?? "";
    if (field && element.module && element.group && element.menu) {
      resultObj[`${key}::${field}`] = [
        {
          module: transElementToStr(element.module),
          section: transElementToStr(element.group),
          menu: transElementToStr(element.menu),
        },
      ];
    } else {
      resultObj[`${key}::${field}`] = [];
    }
  }
  return resultObj;
};

export const addSpace = (str: string) => {
  if (str) {
    return str.split(",").join(", ");
  } else {
    return "";
  }
};

export const sortFn = (a: any, b: any) => {
  return a.order - b.order;
};

export const isLinkField = (
  sinlgeChangeField: Record<string, any>,
  settingsJson: Record<string, any>[],
) => {
  const curChangeTarget = sinlgeChangeField;
  const changeFieldSetting = settingsJson?.find(
    (i) => i.field === curChangeTarget.field,
  );
  const curLinkFieldSetting = changeFieldSetting?.formRules?.linkField;

  return (
    Array.isArray(curLinkFieldSetting) &&
    curLinkFieldSetting?.length > 0 &&
    isEqual(changeFieldSetting?.linkFieldValue, curChangeTarget.value)
  );
};

export const handlePromiseError = (error: any) => {
  const curStatus = error?.response?.status;
  const curMsg = error?.response?.data?.message;
  if (curStatus === 401 || curMsg === "unauthorized") {
    return;
  }
  if (curStatus !== 400 && curStatus !== 500) {
    SnackbarUtils.error(curMsg);
  }
};

export const filterFormData = (
  formData?: Record<string, any>,
  extraFields?: string[],
): Record<string, any> => {
  if (!formData) return {};

  const excludeFields = new Set(
    (extraFields || []).map((field) => field.toLowerCase()),
  );

  return Object.keys(formData).reduce((filteredData, key) => {
    if (!excludeFields.has(key.toLowerCase())) {
      filteredData[key] = formData[key];
    }
    if (includeFields.includes(key.toLowerCase())) {
      filteredData[key] = formData[key];
    }
    return filteredData;
  }, {} as Record<string, any>);
};

/** True when on add/edit/view (or metadata variants) under a side menu, not the first-level menu page. */
export function isMenuRecordSubPage(
  pathname: string,
  routeRecordId?: string | null,
): boolean {
  if (routeRecordId) {
    return true;
  }
  if (/\/add\/?$/.test(pathname)) {
    return true;
  }
  if (/\/addmetadata\//.test(pathname)) {
    return true;
  }
  if (/\/(edit|view)\/[^/]+/.test(pathname)) {
    return true;
  }
  if (/\/(edit|view)metadata\//.test(pathname)) {
    return true;
  }
  return false;
}

export function shouldShowMenuTitleTags(
  pathname: string,
  routeRecordId?: string | null,
): boolean {
  return !isMenuRecordSubPage(pathname, routeRecordId);
}